import { memo } from 'react';
import type { TacticsZone } from '@4cm/core/tactics';

/**
 * Renders the Phase 3 coloured zones for the 2D tactics board.
 *
 * The 2D pitch is LANDSCAPE — its parent <svg> uses the mapping
 * `SVG-x = -world.z, SVG-y = world.x` (see PitchSvg). Every shape
 * therefore swaps + negates accordingly so a zone authored in world
 * metres (e.g. by the Brain) lands at the same pitch position the 3D
 * board would render.
 *
 * Rendered between the grass markings and the player tokens, so coaches
 * see the zones overlaid on the pitch but the tokens still read on top.
 * Opacity: zones carry their own fill (with alpha baked in); we apply
 * a default `opacity={ZONE_OPACITY}` baseline so a fully-opaque fill
 * still reads as a transparent overlay rather than blotting out the
 * grass.
 */
export interface ZoneLayer2DProps {
  zones: TacticsZone[] | undefined;
}

// Baseline opacity floor — even a fully-opaque fill renders at this
// alpha so the pitch markings remain visible. Mirrors the 3D layer's
// material.opacity setting so cross-surface parity holds.
const ZONE_OPACITY = 0.18;

// Label colour — high-contrast neutral so it reads on any fill.
const LABEL_FILL = '#f5f5f5';

function ZoneShape({ zone }: { zone: TacticsZone }) {
  if (zone.shape === 'rect') {
    // geom = [cx, cz, width, depth]
    const [cx, cz, w, d] = zone.geom;
    if (typeof cx !== 'number' || typeof cz !== 'number' || typeof w !== 'number' || typeof d !== 'number') {
      return null;
    }
    // Landscape mapping: world (cx, cz, w, d) → SVG (x=-cz-d/2, y=cx-w/2, w'=d, h'=w).
    // Width-along-x becomes height-along-svg-y, depth-along-z becomes width-along-svg-x.
    return (
      <rect
        x={-cz - d / 2}
        y={cx - w / 2}
        width={d}
        height={w}
        fill={zone.fill}
        opacity={ZONE_OPACITY}
        data-testid={`t2d-zone-${zone.id}`}
        data-shape="rect"
      />
    );
  }
  if (zone.shape === 'circle') {
    // geom = [cx, cz, radius]
    const [cx, cz, r] = zone.geom;
    if (typeof cx !== 'number' || typeof cz !== 'number' || typeof r !== 'number') return null;
    return (
      <circle
        cx={-cz}
        cy={cx}
        r={r}
        fill={zone.fill}
        opacity={ZONE_OPACITY}
        data-testid={`t2d-zone-${zone.id}`}
        data-shape="circle"
      />
    );
  }
  if (zone.shape === 'polygon') {
    // geom = flat [x, z, x, z, ...]
    const pts: string[] = [];
    for (let i = 0; i < zone.geom.length; i += 2) {
      const wx = zone.geom[i];
      const wz = zone.geom[i + 1];
      if (typeof wx !== 'number' || typeof wz !== 'number') return null;
      // Landscape mapping per-vertex.
      pts.push(`${-wz},${wx}`);
    }
    if (pts.length < 3) return null;
    return (
      <polygon
        points={pts.join(' ')}
        fill={zone.fill}
        opacity={ZONE_OPACITY}
        data-testid={`t2d-zone-${zone.id}`}
        data-shape="polygon"
      />
    );
  }
  return null;
}

/**
 * Centroid in world coordinates — used to position the optional label.
 * For rect/circle the centroid is the first two geom values. For
 * polygon we average the vertices (sufficient for convex shapes the
 * coach is likely to draw).
 */
function zoneCentroidWorld(zone: TacticsZone): { x: number; z: number } | null {
  if (zone.shape === 'rect' || zone.shape === 'circle') {
    const cx = zone.geom[0];
    const cz = zone.geom[1];
    if (typeof cx !== 'number' || typeof cz !== 'number') return null;
    return { x: cx, z: cz };
  }
  if (zone.shape === 'polygon') {
    let sx = 0;
    let sz = 0;
    let n = 0;
    for (let i = 0; i < zone.geom.length; i += 2) {
      const wx = zone.geom[i];
      const wz = zone.geom[i + 1];
      if (typeof wx !== 'number' || typeof wz !== 'number') continue;
      sx += wx;
      sz += wz;
      n += 1;
    }
    if (n === 0) return null;
    return { x: sx / n, z: sz / n };
  }
  return null;
}

function ZoneLayer2DInner({ zones }: ZoneLayer2DProps) {
  if (!zones || zones.length === 0) return null;
  return (
    <g className="t2d-zone-layer" data-testid="t2d-zone-layer">
      {zones.map((z) => {
        const centroid = zoneCentroidWorld(z);
        return (
          <g key={z.id} data-zone-id={z.id}>
            <ZoneShape zone={z} />
            {z.label && centroid && (
              <text
                x={-centroid.z}
                y={centroid.x}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={1.8}
                fontWeight={600}
                fill={LABEL_FILL}
                pointerEvents="none"
                style={{ userSelect: 'none', letterSpacing: 0.15 }}
                data-testid={`t2d-zone-label-${z.id}`}
              >
                {z.label}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}

/** Memoised — zones array is stable across token drags so we avoid
 *  re-running the centroid math when only player positions change. */
export const ZoneLayer2D = memo(ZoneLayer2DInner);
